import React, { useState, useEffect } from 'react';
import { Briefcase, Star, Mail, MapPin, Phone, Linkedin, FileText, GraduationCap, Globe, Heart, User, Send, Menu, X, Code, Music, Gamepad, Mountain } from 'lucide-react';

const App = () => {
  const [activeSection, setActiveSection] = useState('about');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleNavClick = (section) => {
    setActiveSection(section);
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header isMobileMenuOpen={isMobileMenuOpen} setIsMobileMenuOpen={setIsMobileMenuOpen} />
      <main className="flex-grow flex flex-col md:flex-row">
        <Sidebar activeSection={activeSection} onNavClick={handleNavClick} isMobileMenuOpen={isMobileMenuOpen} />
        <ContentArea activeSection={activeSection} />
      </main>
      <Footer />
    </div>
  );
};

const Header = ({ isMobileMenuOpen, setIsMobileMenuOpen }) => {
  return (
    <header className="bg-gradient-to-r from-blue-700 to-blue-500 text-white py-4 px-6 md:px-12 sticky top-0 z-50 shadow-md">
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Hassen Daoud</h1>
          <h2 className="text-sm md:text-base text-blue-100">Ingénieur Développement logiciel</h2>
        </div>
        <button
          className="md:hidden block text-white"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
    </header>
  );
};

const Sidebar = ({ activeSection, onNavClick, isMobileMenuOpen }) => {
  const navItems = [
    { id: 'about', label: 'À propos', icon: <User /> },
    { id: 'experience', label: 'Expériences', icon: <Briefcase /> },
    { id: 'education', label: 'Formation', icon: <GraduationCap /> },
    { id: 'skills', label: 'Compétences', icon: <FileText /> },
    { id: 'certifications', label: 'Certifications', icon: <Star /> },
    { id: 'languages', label: 'Langues', icon: <Globe /> },
    { id: 'interests', label: 'Centres d\'intérêts', icon: <Heart /> },
    { id: 'contact', label: 'Contact', icon: <Mail /> }
  ];

  return (
    <nav className={`w-full md:w-64 bg-white shadow-md transition-all duration-300 ${isMobileMenuOpen ? 'fixed inset-0 z-40 pt-16' : 'hidden md:block'}`}>
      <div className="p-4">
        <ul>
          {navItems.map((item) => (
            <li key={item.id} className="mb-3">
              <button
                onClick={() => onNavClick(item.id)}
                className={`w-full text-left flex items-center p-3 rounded-md transition-all duration-200 ${
                  activeSection === item.id
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'hover:bg-blue-50 hover:text-blue-700'
                }`}
              >
                <span className="mr-3">{item.icon}</span>
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

const ContentArea = ({ activeSection }) => {
  return (
    <div className="flex-grow p-4 md:p-8 overflow-y-auto">
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg p-6 md:p-8">
        {activeSection === 'about' && <About />}
        {activeSection === 'experience' && <Experience />}
        {activeSection === 'education' && <Education />}
        {activeSection === 'skills' && <Skills />}
        {activeSection === 'certifications' && <Certifications />}
        {activeSection === 'languages' && <Languages />}
        {activeSection === 'interests' && <Interests />}
        {activeSection === 'contact' && <Contact />}
      </div>
    </div>
  );
};

const About = () => {
  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 text-blue-700 flex items-center">
        <User className="w-6 h-6 mr-2" />
        À propos de moi
      </h2>
      <div className="bg-white rounded-lg">
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
          <div className="md:w-1/3 flex justify-center">
            <div className="w-48 h-48 rounded-full overflow-hidden shadow-md">
              <img 
                src="/images/myPhoto.jpg" 
                alt="Hassen Daoud" 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://via.placeholder.com/200x200.png?text=H";
                }}
              />
            </div>
          </div>
          <div className="md:w-2/3">
            <p className="mb-4 text-lg leading-relaxed">
              Ingénieur en développement logiciel avec plus de 10 ans d'expérience dans la conception, le développement et la maintenance d'applications d'entreprise complexes.
            </p>
            <p className="mb-4 text-lg leading-relaxed">
              Spécialisé en Java/J2EE, je possède une solide expertise dans l'optimisation des performances applicatives, la mise en place d'architectures robustes et l'intégration de systèmes.
            </p>
            <p className="mb-4 text-lg leading-relaxed">
              Ma carrière m'a permis de travailler sur des projets variés dans différents secteurs comme la finance, les télécommunications et le paiement électronique.
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">Java/J2EE</span>
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">Spring</span>
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">C/C++</span>
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">JavaScript</span>
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">Python</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


const Experience = () => {
  const experiences = [
    {
      period: 'Janvier 2022 - à ce jour',
      title: 'Consultant chez SG-CIB Développeur Java/Java EE',
      company: 'BK Consulting',
      description: 'Développement et maintenance de l\'application EProM, un référentiel des produits exotiques :',
      responsibilities: [
        'Maintenance évolutive, support et monitoring de la plateforme en production.',
        'Développement de nouvelles fonctionnalités en EJB et Java EE.',
        'Optimisation des processus métier et amélioration des performances applicatives.',
        'Migration de Tibco vers RabbitMQ pour la communication avec MSD.',
        'Remplacement des appels CORBA par une architecture REST.',
        'Intégration d\'un nouveau connecteur pour le module SDS.',
        'Migration du serveur d\'application de Weblogic vers Wildfly.',
        'Mise en place et automatisation des scans SonarQube pour l\'analyse de la qualité du code.',
        'Migration du gestionnaire d\'artefacts de Nexus vers JFrog Artifactory.',
        'Développement de services web avec Spring.',
        'Implémentation de checks dans l\'application Eprom Health Check en utilisant Spring Boot.'
      ],
      technologies: 'Java/Java EE, Unix/Shell, EJB, Git, Weblogic, Wildfly, Oracle SQL Developer, IntelliJ, JFrog Artifactory, Nexus, SonarQube, Eclipse, REST, Jenkins, MongoDB, HSQLDB, Elasticsearch, Kibana, RabbitMQ, Spring, Spring Boot'
    },
    {
      period: 'Décembre 2018 - Décembre 2021',
      title: 'Consultant-Développeur et intégrateur logiciels',
      company: 'FDJ Gaming Solutions',
      description: 'Faire le suivi de toutes les étapes du projet, depuis le développement, l\'intégration, les tests, jusqu\'à la livraison:',
      responsibilities: [
        'Maintenance évolutive sur un logiciel de paris sportifs pour le client ISBB.',
        'Implémenter des nouvelles APIs au niveau du middleware en utilisant le framework Urban (C/C++).',
        'Implémenter des fonctionnalités au niveau de l\'IHM en utilisant Angular.js.',
        'Développement d\'une couche intermédiaire de communication en Python.',
        'Synchroniser les appels entre la GUI et le middleware en passant par les events et les websockets.',
        'Développer des simulateurs de serveurs en utilisant Golang.',
        'Embarquer des pilotes dans le terminal de jeu et développer l\'interfaçage avec le middleware.',
        'Intégration et livraison des releases à l\'équipe QA pour le projet OLG.',
        'Déployer des nouvelles releases en utilisant l\'outil d\'intégration continue Bamboo.',
        'Configurer Docker sur une des machines de build.',
        'Récupérer les artefacts depuis Nexus à l\'aide de Maven.',
        'Adapter le code de l\'outil d\'automatisation écrit en python.'
      ],
      technologies: 'C/C++, Angular.js, Python, Bamboo, Docker, Golang, Git, Compass, Grunt, Jira, Bitbucket, Confluence, Linux, Fedora, Clonezilla, Nexus, Maven'
    },
    {
      period: 'Juillet 2017 - Septembre 2018',
      title: 'Consultant-Ingénieur Analyste',
      company: 'Ingenico Terminals',
      description: 'Maintenance et évolution des applications de test pour les fonctionnalités intégrées au niveau de la couche SDK pour les terminaux de paiement.',
      responsibilities: [
        'Assurer la communication inter-application à travers des services en faisant appel à des RPCs.',
        'Transfert des données entre les applications en utilisant le format TLV et le format ProtoBuf.',
        'Gérer l\'affichage en utilisant la bibliothèque GOAL.',
        'Développement des scripts Bouchons en utilisant Python et PySerial.',
        'Ecrire des tests d\'automatisation pour des applications Web avec Java et Selenium.',
        'Mise en place d\'un environnement d\'intégration continue.'
      ],
      technologies: 'C/C++, Git, Bitbucket, Nexus, Google ProtoBuf, RPC, Eclipse, Python, pyserial 2.7, Java, Selenium framework, TestNG, Pycharm, Robot Framework, Jenkins, Docker, Oracle VM VirtualBox, Debian, Vagrant, Ant, Testlink, Jira, Confluence'
    },
    {
      period: 'Septembre 2016 - Juillet 2017',
      title: 'Consultant-Software Engineer',
      company: 'HP Entreprise France',
      description: 'Projets OSSM et HPE Service Director Assurance UI pour la surveillance des alarmes dans les réseaux télécoms.',
      responsibilities: [
        'Maintenance évolutive sur la partie backend en utilisant Java/J2EE.',
        'Conception, Intégration, test et personnalisation des widgets en utilisant des fichiers JSON.',
        'Conception et personnalisation des widgets avec le framework HPE UOC (JavaScript, Angular et Nodejs).'
      ],
      technologies: 'Java/J2EE, JUnit, Nodejs, Javascript, Angular, HP ALM, Trello, JSON, GIT, GitHub, Maven, SVN, Jenkins, CouchDB, H2 DB, MariaDB, SQL, HPE UOC, OSSM add-on, OSSM Server, TeMIP, OSSA add-on, UMB'
    },
    {
      period: 'Mai 2015 - Septembre 2016',
      title: 'Software Developer',
      company: 'Proxwel',
      description: 'Développement d\'une solution pour réduire les alarmes dans les réseaux télécoms en utilisant SAP HANA et HP Vertica.',
      responsibilities: [
        'Implémentation des algorithmes statistiques pour l\'identification des règles de corrélation entre les alarmes.',
        'Développement back-end avec SAP HANA et HP Vertica C++ SDK.',
        'Développement front-end avec Server-Side JavaScript, HTML5, CSS et Openui5.',
        'Installation et configuration du Framework TeMIP sur des machines Linux.'
      ],
      technologies: 'C/C++, SAP HANA, Javascript, XSJS, Openui5, D3.js, Angular, Pentaho, HTML5, CSS, Vertica, SQL, Eclipse, Node.JS, Linux, SNMP, MSL, CouchDB'
    },
    {
      period: 'Décembre 2013 - Avril 2015',
      title: 'IT Analyst',
      company: 'Vermeg',
      description: 'Projets de gestion back-office de titres financiers et de gestion du collatéral.',
      responsibilities: [
        'Analyse et développement d\'interfaces pour Attijariwafa Bank.',
        'Conception et développement de modules de calcul du prix, notation financière et décote pour Norges Bank.',
        'Evolution et maintenance sur la partie référentiel (données statiques et dynamiques).',
        'Conception générale et détaillée avec modélisation UML.',
        'Développement backend Java/J2EE avec le framework Palmyra.'
      ],
      technologies: 'Java/J2EE, UML, Oracle, Maven, Nexus, Jenkins, Sonar, JBoss, MySQL, Eclipse TopCased, SVN, SQL Server, Weblogic, WebSphere, MagicDraw'
    }
  ];

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 text-blue-700 flex items-center">
        <Briefcase className="w-6 h-6 mr-2" />
        Expériences Professionnelles
      </h2>
      <div className="relative space-y-0">
        {experiences.map((exp, index) => (
          <div key={index} className="relative">
            {index < experiences.length - 1 && (
              <div className="absolute left-6 top-16 bottom-0 w-1 bg-blue-200"></div>
            )}
            <div className="relative z-10 mb-8">
              <div className="flex">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-lg">
                    <Briefcase size={24} />
                  </div>
                </div>
                <div className="ml-6 bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow border-l-4 border-blue-600 w-full">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4">
                    <h3 className="text-xl font-bold text-blue-700">{exp.title}</h3>
                    <div className="mt-2 lg:mt-0 text-blue-600 font-medium bg-blue-50 px-3 py-1 rounded-full inline-block">
                      {exp.period}
                    </div>
                  </div>
                  <div className="mb-3 text-gray-700 font-semibold text-lg">{exp.company}</div>
                  <p className="mb-4 text-gray-600">{exp.description}</p>
                  <div className="bg-blue-50 p-4 rounded-md mb-4">
                    <h4 className="font-semibold mb-2 text-blue-700">Responsabilités:</h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      {exp.responsibilities.map((resp, idx) => (
                        <li key={idx}>{resp}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-500 mb-2">Technologies et outils:</h4>
                    <div className="flex flex-wrap gap-2">
                      {exp.technologies.split(', ').map((tech, techIdx) => (
                        <span key={techIdx} className="bg-gray-100 text-gray-700 px-2 py-1 rounded-md text-xs">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Education = () => {
  const educations = [
    {
      period: '2010-2013',
      degree: 'Diplôme d\'ingénieur en télécommunications',
      school: 'Sup\'Com',
      mention: 'Mention bien',
      details: [
        'Projet de fin d\'études chez l\'Institut Polytechnique de Bordeaux : Convergence Quadriplay (Internet, TV, VoIP & Services mobiles).',
        'Option Ingénierie Réseaux. Mots clés: Planification et Simulation des réseaux, Gestion et Administration réseaux, QoS dans les réseaux, Migration vers 4G, Réseaux WLAN, Sécurité dans les réseaux.',
        'Unité d\'enseignement aux choix Services télécoms: Introduction à l\'ingénierie des services télécoms, Développement et intégration d\'applications Internet.',
        '10ème rang dans les trois premiers semestres.'
      ]
    },
    {
      period: '2008-2010',
      degree: 'Diplôme de fin de premier cycle',
      school: 'Institut Préparatoire aux Études d\'Ingénieur de Sfax (IPEIS)',
      mention: 'Mention assez bien',
      details: [
        '14ème/839 candidats au concours national.'
      ]
    },
    {
      period: '2008',
      degree: 'Baccalauréat Sciences Techniques',
      school: 'Lycée Hédi Chaker, Sfax-Tunisie',
      mention: 'Mention très bien',
      details: [
        '32ème rang dans le BAC.'
      ]
    }
  ];

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 text-blue-700 flex items-center">
        <GraduationCap className="w-6 h-6 mr-2" />
        Formation
      </h2>
      <div className="grid grid-cols-1 gap-6">
        {educations.map((edu, index) => (
          <div key={index} className="relative overflow-hidden bg-white rounded-lg shadow-md group hover:shadow-lg transition-all">
            <div className="absolute top-0 left-0 w-2 h-full bg-blue-600"></div>
            <div className="p-6 pl-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                <div className="flex items-center">
                  <GraduationCap className="w-8 h-8 text-blue-600 mr-3" />
                  <h3 className="text-xl font-bold text-blue-700">{edu.degree}</h3>
                </div>
                <div className="mt-2 md:mt-0 bg-blue-50 text-blue-600 font-medium px-3 py-1 rounded-full">
                  {edu.period}
                </div>
              </div>
              <div className="mb-2 text-lg font-semibold text-gray-800">{edu.school}</div>
              <div className="mb-4 inline-block bg-green-50 text-green-600 px-2 py-1 rounded-md text-sm font-medium">
                {edu.mention}
              </div>
              <div className="mt-4 bg-gray-50 p-4 rounded-md">
                <ul className="space-y-2">
                  {edu.details.map((detail, idx) => (
                    <li key={idx} className="flex items-start">
                      <div className="flex-shrink-0 mt-1">
                        <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                      </div>
                      <span className="ml-2 text-gray-700">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Skills = () => {
  const skillCategories = [
    {
      category: 'Fonctionnelles',
      icon: <FileText className="w-6 h-6" />,
      color: 'blue',
      skills: [
        'Conception et développement logiciel',
        'Performance optimisation et sécurité des logiciels',
        'Conseils assistance et support technique',
        'Estimation des charges et assurance des livraisons',
        'Monétique & Paiement Electronique',
        'Réseaux et télécommunications (BSS/OSS)',
        'Finance (Gestion backoffice des titres financiers)',
        'Méthodologie Agile (Scrum)',
        'Anglais professionnel'
      ]
    },
    {
      category: 'Langages',
      icon: <Code className="w-6 h-6" />,
      color: 'green',
      skills: [
        'JAVA/J2EE',
        'UML',
        'C/C++',
        'OpenUI5',
        'Server-side Javascript',
        'JavaScript',
        'Angular',
        'Python',
        'Robot framework',
        'Shell Linux',
        'Nodejs',
        'AngularJS',
        'HTML5',
        'CSS',
        'XML'
      ]
    },
    {
      category: 'Outils',
      icon: <Star className="w-6 h-6" />,
      color: 'purple',
      skills: [
        'Eclipse',
        'TestLink',
        'Trello',
        'Jira',
        'Confluence',
        'Bitbucket',
        'TortoiseGit',
        'TortoiseSVN',
        'Netbeans',
        'Visual Studio Community 2015',
        'SOAPUI',
        'HP ALM',
        'Oracle SQL Developer',
        'MySQL Workbench',
        'JBoss',
        'WebSphere',
        'WebLogic',
        'Hudson',
        'Jenkins',
        'SonarQube',
        'Topcased',
        'MagicDraw',
        'RapidSVN'
      ]
    }
  ];

  const getColorClasses = (color) => {
    switch (color) {
      case 'blue':
        return {
          bgLight: 'bg-blue-50',
          bgDark: 'bg-blue-600',
          text: 'text-blue-700',
          border: 'border-blue-200',
          tag: 'bg-blue-100 text-blue-800'
        };
      case 'green':
        return {
          bgLight: 'bg-green-50',
          bgDark: 'bg-green-600',
          text: 'text-green-700',
          border: 'border-green-200',
          tag: 'bg-green-100 text-green-800'
        };
      case 'purple':
        return {
          bgLight: 'bg-purple-50',
          bgDark: 'bg-purple-600',
          text: 'text-purple-700',
          border: 'border-purple-200',
          tag: 'bg-purple-100 text-purple-800'
        };
      default:
        return {
          bgLight: 'bg-gray-50',
          bgDark: 'bg-gray-600',
          text: 'text-gray-700',
          border: 'border-gray-200',
          tag: 'bg-gray-100 text-gray-800'
        };
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 text-blue-700 flex items-center">
        <FileText className="w-6 h-6 mr-2" />
        Compétences
      </h2>
      <div className="space-y-8">
        {skillCategories.map((category, index) => {
          const colors = getColorClasses(category.color);
          return (
            <div key={index} className="rounded-lg overflow-hidden shadow-md">
              <div className={`p-4 ${colors.bgDark} text-white flex items-center`}>
                <div className="mr-3">{category.icon}</div>
                <h3 className="text-xl font-bold">{category.category}</h3>
              </div>
              <div className={`p-6 ${colors.bgLight} border-t-0 border ${colors.border}`}>
                <div className="flex flex-wrap gap-2">
                  {category.skills.map((skill, idx) => (
                    <span 
                      key={idx} 
                      className={`${colors.tag} px-3 py-1 rounded-full text-sm font-medium transition-transform hover:scale-105`}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Certifications = () => {
  const certifications = [
    {
      title: 'Java Standard Edition 6',
      detail: 'Certifié OCJP 96%',
      icon: <Code />,
      iconBg: 'bg-red-500'
    },
    {
      title: 'Cisco',
      detail: 'Certifié CCNA 1, CCNA 2, CCNA 3 & CCNA 4',
      icon: <Globe />,
      iconBg: 'bg-blue-500'
    },
    {
      title: 'Linux',
      detail: 'Certifié Novell CLA 11, LPIC-1, LPIC-2',
      icon: <FileText />,
      iconBg: 'bg-green-500'
    },
    {
      title: 'LTE',
      detail: 'Certifié TCLS-1 TeKonsult',
      icon: <Phone />,
      iconBg: 'bg-purple-500'
    },
    {
      title: 'Anglais',
      detail: 'Certifié TOIEC Score 765/990',
      icon: <Globe />,
      iconBg: 'bg-yellow-500'
    },
    {
      title: 'Français',
      detail: 'Certifié TCF Score/Niveau 526/C1',
      icon: <Globe />,
      iconBg: 'bg-blue-500'
    }
  ];

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 text-blue-700 flex items-center">
        <Star className="w-6 h-6 mr-2" />
        Certifications
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {certifications.map((cert, index) => (
          <div key={index} className="relative bg-white rounded-lg shadow-md group hover:shadow-lg transition-all overflow-hidden">
            <div className="absolute h-1 w-full top-0 left-0 bg-gradient-to-r from-blue-500 to-purple-500"></div>
            <div className="p-6 pt-8">
              <div className="flex items-start mb-4">
                <div className={`${cert.iconBg} text-white p-3 rounded-lg mr-4`}>
                  {cert.icon}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800">{cert.title}</h3>
                  <p className="text-gray-600 mt-1">{cert.detail}</p>
                </div>
              </div>
            </div>
            <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Languages = () => {
  const languages = [
    { language: 'Arabe', level: 'Langue maternelle', percentage: 100 },
    { language: 'Français', level: 'Courant (TCF 526/C1)', percentage: 95 },
    { language: 'Anglais', level: 'Courant (TOEIC 765)', percentage: 80 },
    { language: 'Allemand', level: 'Notions', percentage: 25 }
  ];

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 text-blue-700 flex items-center">
        <Globe className="w-6 h-6 mr-2" />
        Langues
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {languages.map((lang, index) => (
          <div key={index} className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-bold text-gray-800">{lang.language}</h3>
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                {lang.level}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1">
              <div 
                className="bg-gradient-to-r from-blue-500 to-blue-700 h-2.5 rounded-full" 
                style={{ width: `${lang.percentage}%` }}
              ></div>
            </div>
            <div className="text-right text-xs text-gray-500">{lang.percentage}%</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Interests = () => {
  const interests = [
    { name: 'Musique', icon: <Music className="w-8 h-8" /> },
    { name: 'Jeux vidéo', icon: <Gamepad className="w-8 h-8" /> },
    { name: 'Excursions', icon: <Mountain className="w-8 h-8" /> }
  ];

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 text-blue-700 flex items-center">
        <Heart className="w-6 h-6 mr-2" />
        Centres d'intérêts
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {interests.map((interest, index) => (
          <div key={index} className="flex flex-col items-center justify-center bg-white p-8 rounded-lg shadow-md text-center hover:shadow-lg transition-shadow">
            <div className="mb-4 text-blue-600 bg-blue-50 p-4 rounded-full">
              {interest.icon}
            </div>
            <span className="text-xl font-semibold text-gray-800">{interest.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Contact = () => {
  const [formValues, setFormValues] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [status, setStatus] = useState({
    submitted: false,
    submitting: false,
    info: { error: false, msg: null }
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  const handleResponse = (status, msg) => {
    if (status === 200) {
      setStatus({
        submitted: true,
        submitting: false,
        info: { error: false, msg: msg }
      });
      setFormValues({
        name: '',
        email: '',
        message: ''
      });
    } else {
      setStatus({
        info: { error: true, msg: msg }
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus(prevStatus => ({ ...prevStatus, submitting: true }));
    
    fetch("https://formspree.io/f/mrbqqzkn", {  // Replace with your Formspree ID
      method: "POST",
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formValues)
    })
      .then(response => {
        response.json().then(data => {
          handleResponse(response.status, data.message);
        });
      })
      .catch(error => {
        handleResponse(500, error.message);
      });
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 text-blue-700 flex items-center">
        <Mail className="w-6 h-6 mr-2" />
        Contact
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <h3 className="text-xl font-semibold mb-4">Mes coordonnées</h3>
          <div className="space-y-4">
            <div className="flex items-center">
              <MapPin className="w-5 h-5 mr-3 text-blue-600" />
              <span>31 Rue De Thionville, 75019 Paris</span>
            </div>
            <div className="flex items-center">
              <Phone className="w-5 h-5 mr-3 text-blue-600" />
              <span>+33 (0)7 69 08 46 50</span>
            </div>
            <div className="flex items-center">
              <Mail className="w-5 h-5 mr-3 text-blue-600" />
              <span>hassendaoud@gmail.com</span>
            </div>
            <div className="flex items-center">
              <Linkedin className="w-5 h-5 mr-3 text-blue-600" />
              <span>www.linkedin.com/in/hassen-daoud</span>
            </div>
          </div>
        </div>
        <div>
          <h3 className="text-xl font-semibold mb-4">Envoyez-moi un message</h3>
          
          {status.submitted ? (
            <div className="p-4 bg-green-50 text-green-700 rounded-md">
              <p className="font-medium">Message envoyé avec succès!</p>
              <p className="mt-2">Merci de m'avoir contacté. Je vous répondrai dans les plus brefs délais.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formValues.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={status.submitting}
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formValues.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={status.submitting}
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formValues.message}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={status.submitting}
                ></textarea>
              </div>
              
              {status.info.error && (
                <div className="p-3 bg-red-50 text-red-700 rounded-md">
                  <p className="text-sm">Une erreur est survenue. Veuillez réessayer plus tard.</p>
                </div>
              )}
              
              <button
                type="submit"
                className={`inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                  status.submitting ? 'opacity-75 cursor-not-allowed' : ''
                }`}
                disabled={status.submitting}
              >
                {status.submitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" /> Envoyer
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const Footer = () => {
  return (
    <footer className="bg-gray-800 text-white py-6 px-6">
      <div className="max-w-6xl mx-auto text-center">
        <div className="flex justify-center space-x-4 mb-4">
          <a 
            href="https://www.linkedin.com/in/hassen-daoud" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="hover:text-blue-400 transition-colors"
            aria-label="LinkedIn Profile"
          >
            <Linkedin className="w-6 h-6" />
          </a>
          <a 
            href="mailto:hassendaoud@gmail.com" 
            className="hover:text-blue-400 transition-colors"
            aria-label="Send Email"
          >
            <Mail className="w-6 h-6" />
          </a>
        </div>
        <p>© {new Date().getFullYear()} Hassen Daoud - Tous droits réservés</p>
      </div>
    </footer>
  );
};

export default App;
